import { useMemo, useState } from "react";

import { authoring as authoringApi, questionLibrary } from "../api/resources";
import { QuestionEditor } from "../components/QuestionEditor";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Loading,
  Modal,
  PageHeader,
  Stat,
  StatusBadge,
  Tabs,
} from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { useAsync } from "../lib/useAsync";
import type { LibraryQuestion } from "../lib/types";

export function QuestionLibraryPage() {
  const [selectedModule, setSelectedModule] = useState<string | undefined>();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"questions" | "expectations" | "rules" | "history">("questions");
  const [syncMessage, setSyncMessage] = useState<string | undefined>();
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState<LibraryQuestion | "new" | undefined>();
  const [exported, setExported] = useState<Record<string, unknown> | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [showRetired, setShowRetired] = useState(false);

  const { organizationId } = useCurrentOrganization();
  const { can } = useAuth();
  const mayEdit = can(organizationId, "library:write");

  const modules = useAsync(() => questionLibrary.modules({}), [nonce]);
  const validation = useAsync(() => questionLibrary.validate(), [syncMessage]);
  const detail = useAsync(
    () => (selectedModule ? questionLibrary.module(selectedModule) : Promise.resolve(undefined as never)),
    [selectedModule, nonce],
  );
  const history = useAsync(
    () =>
      selectedModule
        ? authoringApi.audit({ module_key: selectedModule, limit: 50 })
        : Promise.resolve([]),
    [selectedModule, nonce],
  );

  async function run(action: () => Promise<unknown>) {
    setError(undefined);
    try {
      await action();
      setNonce((n) => n + 1);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  const rows = modules.data ?? [];
  const active = detail.data;

  const questions = useMemo(() => {
    if (!active) return [];
    const needle = search.trim().toLowerCase();
    return active.questions.filter((question) => {
      if (!showRetired && !question.is_active) return false;
      if (!needle) return true;
      return (
        question.semantic_id.toLowerCase().includes(needle) ||
        question.title.toLowerCase().includes(needle)
      );
    });
  }, [active, search, showRetired]);

  const totalQuestions = rows.reduce((sum, module) => sum + module.question_count, 0);

  return (
    <>
      <PageHeader
        title="Question library"
        description="Modules are versioned data, not code. Every question has a stable semantic ID so answers stay machine-usable."
        actions={
          <button
            onClick={async () => {
              const result = await questionLibrary.sync();
              setSyncMessage(
                `Synced ${result.modules} modules and ${result.questions} questions from disk.`,
              );
              modules.reload();
            }}
          >
            Reload definitions
          </button>
        }
      />

      <div className="stack">
        <ErrorBanner error={error ?? modules.error} />
        {syncMessage ? <div className="banner banner--ok">{syncMessage}</div> : null}
        {validation.data && !validation.data.valid ? (
          <div className="banner banner--error">
            Library invalid: {validation.data.errors.slice(0, 3).join("; ")}
          </div>
        ) : null}

        <div className="grid grid--stats">
          <Stat label="Modules" value={rows.length} />
          <Stat label="Questions" value={totalQuestions} />
          <Stat
            label="Organization modules"
            value={rows.filter((module) => module.applies_to === "organization").length}
          />
          <Stat
            label="Validation"
            value={validation.data?.valid ? "valid" : "invalid"}
            tone={validation.data?.valid ? "ok" : "danger"}
          />
        </div>

        <div className="grid" style={{ gridTemplateColumns: "300px 1fr", alignItems: "start" }}>
          <Card title="Modules" flush>
            {modules.loading ? (
              <Loading />
            ) : (
              <div style={{ maxHeight: 620, overflowY: "auto" }}>
                {rows.map((module) => (
                  <button
                    key={module.key}
                    onClick={() => setSelectedModule(module.key)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      borderBottom: "1px solid var(--border)",
                      borderRadius: 0,
                      background:
                        selectedModule === module.key ? "var(--accent-soft)" : "transparent",
                      padding: "10px 14px",
                    }}
                  >
                    <strong>{module.name}</strong>
                    <div className="tiny faint mono">{module.key}</div>
                    <div className="tiny muted">
                      {module.question_count} questions · {module.applies_to}
                      {module.industry_scope.length ? ` · ${module.industry_scope.join(", ")}` : ""}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>

          <div className="stack">
            {!active ? (
              <Card>
                <p className="muted">Select a module to inspect its questions.</p>
              </Card>
            ) : (
              <>
                <Card
                  title={active.name}
                  actions={
                    <>
                      <input
                        style={{ width: 240 }}
                        placeholder="Filter by semantic ID or title"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                      />
                      <button
                        onClick={() =>
                          void run(async () => {
                            const result = await authoringApi.exportModule(active.key);
                            setExported(result.definition);
                          })
                        }
                        title="The JSON to commit back into version control"
                      >
                        Export JSON
                      </button>
                      {mayEdit ? (
                        <button className="primary" onClick={() => setEditing("new")}>
                          Add question
                        </button>
                      ) : null}
                    </>
                  }
                >
                  <p className="muted" style={{ marginTop: 0 }}>{active.description}</p>
                  <div className="row">
                    <span className="badge badge--neutral">v{active.version}</span>
                    <span className="badge badge--neutral">{active.applies_to}</span>
                    {active.pages.map((page) => (
                      <span key={page.key} className="badge badge--accent">
                        {page.title}
                      </span>
                    ))}
                  </div>
                  {active.source === "database" ? (
                    <p className="tiny faint" style={{ marginBottom: 0, marginTop: 8 }}>
                      Edited here, so this module is now owned by the database and “Reload
                      definitions” leaves it alone
                      {active.updated_by ? ` (last change by ${active.updated_by})` : ""}. Export
                      the JSON and commit it to bring the files back in step.
                    </p>
                  ) : (
                    <p className="tiny faint" style={{ marginBottom: 0, marginTop: 8 }}>
                      Owned by the JSON definitions on disk. The first edit made here takes
                      ownership, so a redeploy cannot quietly undo it.
                    </p>
                  )}
                </Card>

                <Tabs
                  value={tab}
                  onChange={setTab}
                  options={[
                    { value: "questions", label: "Questions", count: active.questions.length },
                    {
                      value: "expectations",
                      label: "Knowledge expectations",
                      count: active.knowledge_expectations.length,
                    },
                    {
                      value: "rules",
                      label: "Follow-up rules",
                      count: active.follow_up_rules.length,
                    },
                    { value: "history", label: "Change history", count: history.data?.length ?? 0 },
                  ]}
                />

                {tab === "questions" ? (
                  <Card
                    flush
                    actions={
                      <label className="row tiny" style={{ gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={showRetired}
                          onChange={(event) => setShowRetired(event.target.checked)}
                        />
                        Show retired
                      </label>
                    }
                  >
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Semantic ID</th>
                            <th>Question</th>
                            <th>Type</th>
                            <th>Page</th>
                            <th>Predicate</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {questions.map((question) => (
                            <tr key={question.id}>
                              <td className="mono small">
                                {question.parent_question_id ? "↳ " : ""}
                                {question.semantic_id}
                              </td>
                              <td>
                                {question.title}
                                {question.description ? (
                                  <div className="tiny faint truncate">{question.description}</div>
                                ) : null}
                              </td>
                              <td className="small muted">{question.question_type_code}</td>
                              <td className="small muted">{question.page_key ?? "—"}</td>
                              <td className="small mono">
                                {String(question.knowledge_target?.predicate ?? "—")}
                              </td>
                              <td>
                                <div className="row" style={{ gap: 4 }}>
                                  {question.is_required ? (
                                    <span className="badge badge--required">required</span>
                                  ) : null}
                                  {!question.extractable ? (
                                    <span className="badge badge--neutral">not extracted</span>
                                  ) : null}
                                  {Object.keys(question.depends_on ?? {}).length ? (
                                    <span className="badge badge--accent">conditional</span>
                                  ) : null}
                                  {!question.is_active ? (
                                    <span className="badge badge--rejected">retired</span>
                                  ) : null}
                                  {mayEdit ? (
                                    <>
                                      <button
                                        className="btn--ghost tiny"
                                        onClick={() => setEditing(question)}
                                      >
                                        Edit
                                      </button>
                                      {question.is_active ? (
                                        <button
                                          className="btn--ghost tiny"
                                          title="Stop asking this, without losing what it already collected"
                                          onClick={() =>
                                            void run(() =>
                                              authoringApi.retireQuestion(
                                                active.key,
                                                question.semantic_id,
                                              ),
                                            )
                                          }
                                        >
                                          Retire
                                        </button>
                                      ) : (
                                        <button
                                          className="btn--ghost tiny"
                                          onClick={() =>
                                            void run(() =>
                                              authoringApi.restoreQuestion(
                                                active.key,
                                                question.semantic_id,
                                              ),
                                            )
                                          }
                                        >
                                          Restore
                                        </button>
                                      )}
                                    </>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                ) : null}

                {tab === "expectations" ? (
                  <Card flush>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Key</th>
                            <th>What we expect to learn</th>
                            <th>Severity</th>
                            <th>Per record</th>
                            <th>Suggested question</th>
                          </tr>
                        </thead>
                        <tbody>
                          {active.knowledge_expectations.map((expectation, index) => (
                            <tr key={index}>
                              <td className="mono small">{String(expectation.key)}</td>
                              <td>{String(expectation.title)}</td>
                              <td>
                                <StatusBadge status={String(expectation.severity)} />
                              </td>
                              <td className="small muted">{String(expectation.per ?? "—")}</td>
                              <td className="mono small">
                                {String(expectation.suggested_question ?? "—")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                ) : null}

                {tab === "history" ? (
                  <Card flush>
                    {history.loading ? (
                      <Loading />
                    ) : !history.data?.length ? (
                      <EmptyState
                        title="No changes yet"
                        description="Every edit to a question is recorded here with who made it, what it was before, and why."
                      />
                    ) : (
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th>When</th>
                              <th>Question</th>
                              <th>Change</th>
                              <th>Who</th>
                              <th>Why</th>
                            </tr>
                          </thead>
                          <tbody>
                            {history.data.map((entry) => (
                              <tr key={entry.id}>
                                <td className="small muted">
                                  {entry.created_at
                                    ? new Date(entry.created_at).toLocaleString()
                                    : "—"}
                                </td>
                                <td className="mono small">{entry.semantic_id ?? "—"}</td>
                                <td>
                                  <StatusBadge status={entry.action} />
                                  {entry.old_values.title || entry.new_values.title ? (
                                    <div className="tiny faint truncate">
                                      {String(entry.old_values.title ?? "")} →{" "}
                                      {String(entry.new_values.title ?? "")}
                                    </div>
                                  ) : null}
                                </td>
                                <td className="small muted">{entry.actor ?? "—"}</td>
                                <td className="small muted">{entry.reason ?? "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                ) : null}

                {tab === "rules" ? (
                  <div className="stack">
                    {active.follow_up_rules.map((rule, index) => (
                      <Card key={index} title={String(rule.key)}>
                        <p style={{ marginTop: 0 }}>{String(rule.ask)}</p>
                        <p className="small muted">{String(rule.reason ?? "")}</p>
                        <div className="evidence">
                          <div className="evidence__ref">when</div>
                          <pre className="mono" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                            {JSON.stringify(rule.when, null, 2)}
                          </pre>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      {editing && active ? (
        <QuestionEditor
          moduleKey={active.key}
          question={editing === "new" ? undefined : editing}
          pages={active.pages}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {exported ? (
        <Modal
          title="Commit this back into version control"
          onClose={() => setExported(undefined)}
          footer={
            <button
              onClick={() =>
                void navigator.clipboard?.writeText(JSON.stringify(exported, null, 2))
              }
            >
              Copy JSON
            </button>
          }
        >
          <p className="small muted">
            Save this as <span className="mono">app/question_library/definitions/{String(exported.key)}.json</span>{" "}
            so the change is reviewed in a diff like any other product change.
          </p>
          <pre className="mono small" style={{ maxHeight: 360, overflow: "auto" }}>
            {JSON.stringify(exported, null, 2)}
          </pre>
        </Modal>
      ) : null}
    </>
  );
}
